import { useContext, useMemo, useState } from "react";
import Delete from "@mui/icons-material/Delete";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Star from "@mui/icons-material/Star";
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { FavouriteRecipient } from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import { removeFavouriteRecipient } from "../../helpers/favouriteRecipients.js";
import { routeFns } from "../../routePaths.js";
import type { DefaultPageProps } from "../defaultProps.js";
import {
  Button,
  FormTitle,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { stringAvatar } from "~/components/layout/menu/helpers/index.js";
import { Container } from "~/components/layout/styleWrappers.js";
import { logActionToDatadog } from "~/tracing.js";

type GroupedFavourites = {
  dataControllerId: string;
  dataControllerName: string;
  isActive: boolean;
  favourites: FavouriteRecipient[];
};

// The same team or user can be a favourite under more than one data controller.
const favouriteKey = (favourite: FavouriteRecipient): string =>
  `${favourite.dataControllerId}-${favourite.teamId}-${favourite.userId ?? "team"}`;

export function FavouriteRecipientsPage(_props: DefaultPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = useContext(LoggedInUserContext);

  // Track which sections are expanded - active one is expanded by default
  const [expandedSections, setExpandedSections] = useState<Set<string> | null>(
    null,
  );

  const [removalsInFlight, setRemovalsInFlight] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const user = userContext?.user;
  const refetchUser = userContext?.refetchUser;
  const favouriteRecipients = user?.favouriteRecipients ?? [];
  const activeDataControllerId = user?.sessionContext?.dataControllerId;

  // Group favourites by data controller, sorted with active first
  const groupedFavourites = useMemo((): GroupedFavourites[] => {
    const groups = new Map<string, GroupedFavourites>();

    for (const fav of favouriteRecipients) {
      const existing = groups.get(fav.dataControllerId);
      if (existing) {
        existing.favourites.push(fav);
      } else {
        groups.set(fav.dataControllerId, {
          dataControllerId: fav.dataControllerId,
          dataControllerName: fav.dataControllerName,
          isActive: fav.dataControllerId === activeDataControllerId,
          favourites: [fav],
        });
      }
    }

    // Sort: active data controller first, then alphabetically
    return Array.from(groups.values()).sort((a, b) => {
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return a.dataControllerName.localeCompare(b.dataControllerName);
    });
  }, [favouriteRecipients, activeDataControllerId]);

  if (!userContext || !user || !refetchUser) {
    return null;
  }

  const userId = user.id;

  // Check if a section is expanded
  const isExpanded = (dataControllerId: string): boolean => {
    // If user hasn't toggled anything yet, active section should be expanded
    if (expandedSections === null) {
      return dataControllerId === activeDataControllerId;
    }
    return expandedSections.has(dataControllerId);
  };

  const toggleSection = (dataControllerId: string): void => {
    setExpandedSections((prev) => {
      // Initialize the set on first interaction
      if (prev === null) {
        // If clicking the active section, collapse it (return empty set)
        // If clicking another section, keep active expanded and add the new one
        if (dataControllerId === activeDataControllerId) {
          return new Set<string>();
        } else {
          const newSet = new Set<string>();
          if (activeDataControllerId) {
            newSet.add(activeDataControllerId);
          }
          newSet.add(dataControllerId);
          return newSet;
        }
      }

      // Normal toggle behavior
      const newSet = new Set(prev);
      if (newSet.has(dataControllerId)) {
        newSet.delete(dataControllerId);
      } else {
        newSet.add(dataControllerId);
      }
      return newSet;
    });
  };

  const handleRemoveFavourite = async (favourite: FavouriteRecipient) => {
    const key = favouriteKey(favourite);
    setRemovalsInFlight((prev) => new Set(prev).add(key));
    try {
      const removed = await removeFavouriteRecipient(
        favourite.dataControllerId,
        favourite.teamId,
        favourite.userId,
        refetchUser,
      );
      if (!removed) return;
      logActionToDatadog("favourite_recipient_removed", {
        dataControllerId: favourite.dataControllerId,
        dataControllerName: favourite.dataControllerName,
        teamId: favourite.teamId,
        teamName: favourite.teamName,
        userId: favourite.userId,
        userName: favourite.userName,
      });
      renderSuccessToast({
        message: t("pages.favouriteRecipients.removedSuccess"),
      });
    } finally {
      setRemovalsInFlight((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const getDisplayName = (favourite: FavouriteRecipient): string => {
    if (favourite.userName) {
      return `${favourite.userName} (${favourite.teamName})`;
    }
    return favourite.teamName;
  };

  const getDisplayEmail = (favourite: FavouriteRecipient): string => {
    return favourite.userEmail ?? favourite.teamEmail;
  };

  return (
    <>
      <Container addMarginBottom>
        <FormTitle
          useReducedTopPadding
          titleText={t("pages.favouriteRecipients.title")}
          subtitleText={t("pages.favouriteRecipients.subtitle")}
        />

        {favouriteRecipients.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              {t("pages.favouriteRecipients.emptyState")}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {groupedFavourites.map((group) => (
              <Paper key={group.dataControllerId}>
                <ListItemButton
                  onClick={() => toggleSection(group.dataControllerId)}
                  sx={{
                    backgroundColor: group.isActive
                      ? "primary.main"
                      : "transparent",
                    color: group.isActive ? "primary.contrastText" : "inherit",
                    "&:hover": {
                      backgroundColor: group.isActive
                        ? "primary.dark"
                        : "action.hover",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={group.isActive ? "bold" : "normal"}
                          color="inherit"
                        >
                          {group.dataControllerName}
                        </Typography>
                        {group.isActive && (
                          <Chip
                            label={t(
                              "pages.favouriteRecipients.activeOrganisation",
                            )}
                            size="small"
                            color="default"
                            icon={<Star fontSize="small" />}
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              color: "primary.main",
                              "& .MuiChip-icon": { color: "primary.main" },
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={group.isActive ? "inherit" : "text.secondary"}
                          sx={{
                            ml: "auto",
                            mr: 1,
                            opacity: group.isActive ? 0.9 : 1,
                          }}
                        >
                          {t("pages.favouriteRecipients.countLabel", {
                            count: group.favourites.length,
                          })}
                        </Typography>
                      </Box>
                    }
                  />
                  {isExpanded(group.dataControllerId) ? (
                    <ExpandLess
                      sx={{ color: group.isActive ? "inherit" : undefined }}
                    />
                  ) : (
                    <ExpandMore
                      sx={{ color: group.isActive ? "inherit" : undefined }}
                    />
                  )}
                </ListItemButton>
                <Collapse
                  in={isExpanded(group.dataControllerId)}
                  timeout="auto"
                  unmountOnExit
                >
                  {!group.isActive && <Divider />}
                  <List disablePadding>
                    {group.favourites.map((favourite, index) => (
                      <ListItem
                        key={favouriteKey(favourite)}
                        divider={index < group.favourites.length - 1}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label={t("pages.favouriteRecipients.remove")}
                            disabled={removalsInFlight.has(
                              favouriteKey(favourite),
                            )}
                            onClick={() => handleRemoveFavourite(favourite)}
                          >
                            <Delete />
                          </IconButton>
                        }
                        sx={{ pl: 4 }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{ backgroundColor: "primary.main" }}
                            {...stringAvatar(getDisplayName(favourite))}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={getDisplayName(favourite)}
                          secondary={getDisplayEmail(favourite)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Paper>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            label={t("pages.favouriteRecipients.backToProfile")}
            onClick={() => navigate(routeFns.userProfilePage(userId))}
          />
        </Box>
      </Container>
    </>
  );
}
